import i18n from 'i18next';
import loadjs from 'loadjs';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import PropTypes from 'prop-types';
import React from 'react';
import { hot } from 'react-hot-loader/root';
import styled, { createGlobalStyle } from 'styled-components';

import ChatWindow from './components/ChatWindow';
import ErrorHandler from './components/ErrorHandler';
import Footer from './components/Footer';
import Header from './components/Header';
import HeaderMenu from './components/HeaderMenu';
import Launcher from './components/Launcher';
import LiveChat from './components/LiveChat';
import Loading from './components/Loading/Loading';
import MessageInput from './components/MessageInput';
import MessageList from './components/MessageList';
import OverlapMenu from './components/OverlapMenu';
import SubHeader from './components/SubHeader';
import Transcript from './components/Transcript';
import { useAppInitialized } from './hooks';
import { StoreProvider } from './stores';
import { DEFAULT_IMPORT_FONT_FAMILY } from './utilities';

const App = ({ config = {} }) => {
  useAppInitialized();
  const [isReady, setReady] = React.useState(false);

  // Set up fonts
  // TODO: change this useEffect after removing config.importFontFamily
  React.useEffect(() => {
    if (config.fonts && config.fonts.length) {
      const enabledFonts = config.fonts.filter(font => font.enabled);

      if (enabledFonts.length) {
        forEach(enabledFonts, font => {
          loadjs(`css!${font.importFontFamily}`);
        });
      } else {
        loadjs(`css!${DEFAULT_IMPORT_FONT_FAMILY}`);
      }

      return;
    }

    loadjs(`css!${config.importFontFamily}`);
  }, [config.fonts, config.importFontFamily]);

  // load additional styles (for hotfix)
  React.useEffect(() => {
    loadjs(`css!${config.apiURL}/CFP2/css/main.css`);
  }, [config.apiURL]);

  i18n.on('initialized', () => {
    setReady(true);
  });

  React.useEffect(() => {
    if (config.page && config.page.backgroundImage && window) {
      // allowed URL
      const configUrl = find(config.page.allowedURLs, url => url === window.location.origin);

      // check if the page is the admin page
      const isAdminPage = includes(window.location.href, '/CSS/aqua/admin/va-settings.aspx');

      // apply the background-image
      if (configUrl && !isAdminPage) {
        const bodyElement = document.querySelector('body');
        if (bodyElement) {
          bodyElement.style.backgroundImage = `url(${config.page.backgroundImage})`;
          bodyElement.style.backgroundRepeat = 'no-repeat';
          bodyElement.style.backgroundPosition = 'left top';
          bodyElement.style.backgroundSize = 'cover';
          bodyElement.style.backgroundAttachment = 'fixed';
        }
      }
    }
  }, [config.page]);
  return (
    <StoreProvider config={config}>
      <ChatWindow>
        <ErrorHandler>
          <React.Suspense fallback={null}>
            {config.overlapMenu && config.overlapMenu.enabled ? (
              <OverlapMenu />
            ) : (
              <Wrapper
                isFooter={config.footer.enabled && !config.copyright.floatingText}
                boxShadow={config.window.boxShadow}
              >
                <Header />
                <Content background={config.window.background} boxShadow={config.window.boxShadow}>
                  {isReady ? (
                    <>
                      {config.subheader.enabled && <SubHeader />}
                      {config.headerMenu?.enabled && <HeaderMenu />}
                      <LiveChat />
                      <Transcript />
                      <MessageList />
                      <MessageInput />
                    </>
                  ) : (
                    <LoaderWrapper>
                      <Loading text="" color={config.window.colors.accent} size={14} />
                    </LoaderWrapper>
                  )}
                </Content>
                {config.footer.enabled && <Footer />}
              </Wrapper>
            )}
          </React.Suspense>
        </ErrorHandler>
      </ChatWindow>
      <Launcher />
      <GlobalStyle />
    </StoreProvider>
  );
};

App.propTypes = {
  config: PropTypes.shape({
    window: PropTypes.shape({
      background: PropTypes.string.isRequired,
      boxShadow: PropTypes.string,
      borderRadius: PropTypes.number
    }).isRequired,
    id: PropTypes.number.isRequired,
    source: PropTypes.string.isRequired,
    module: PropTypes.string.isRequired
  }).isRequired
};

const GlobalStyle = createGlobalStyle`
  /* box-sizing: border-box; */

  /*
    These styles are necessary
    for the cursor styles
    to be applied correctly for the
    disabled and enabled drag node.
  */
  .react-rnd {
    &-draggable {
      cursor: move;

      &-cancel {
        cursor: auto;
      }
    }
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  width: 100%;
  min-width: 100%;

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    display: ${({ isFooter }) => (isFooter ? 'none' : 'block')};
    width: 100%;
    height: 1px;
    background: transparent;
    box-shadow: ${({ isFooter, boxShadow }) => (isFooter ? 'none' : boxShadow)};
  }

  /* Temporary fix for MS Edge */
  @supports (-ms-ime-align: auto) {
    position: fixed;
  }
`;

const Content = styled.main`
  position: relative;
  top: -1px;
  right: auto;
  bottom: auto;
  left: auto;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  width: auto;
  min-width: auto;
  height: auto;
  min-height: auto;
  background: ${props => props.background};
  border: none;
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: -1;
    display: block;
    width: 1px;
    height: 100%;
    background: transparent;
    box-shadow: ${props => props.boxShadow};
  }
  &::before {
    left: 0px;
  }
  &::after {
    right: 0px;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export default hot(App);
